<template>
  <div class="tw:relative" v-click-outside="closePopover">
    <button
      @click="togglePopover"
      class="tw:w-9 tw:h-9 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-900 tw:border tw:border-slate-800/40 tw:transition-colors tw:shrink-0 tw:relative tw:cursor-pointer"
    >
      <Bell class="tw:w-4.5 tw:h-4.5" />
      <span
        v-if="summaryCountDisplay"
        class="tw:absolute tw:top-[1px] tw:right-[1px] tw:min-w-3.5 tw:h-3.5 tw:px-1 tw:rounded-full tw:bg-rose-500 tw:ring-2 tw:ring-slate-950 tw:flex tw:items-center tw:justify-center tw:text-2xs tw:font-bold tw:text-white tw:leading-none"
      >
        {{ summaryCountDisplay }}
      </span>
    </button>

    <Transition name="dropdown">
      <div
        v-if="popoverOpen"
        class="tw:absolute tw:right-0 tw:mt-2 tw:w-80 tw:sm:w-96 tw:max-h-[calc(100vh-5rem)] tw:overflow-y-auto tw:rounded-xl tw:bg-slate-900 tw:border tw:border-slate-800/80 tw:shadow-premium tw:p-4 tw:z-50 tw:text-left tw:text-slate-200"
      >
        <div class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-slate-800/60 tw:pb-3 tw:mb-3">
          <span class="tw:font-semibold tw:text-sm tw:text-slate-100">Notifications</span>
          <button
            v-if="notificationMessageItems?.length > 0"
            :disabled="deletingAllNotificationMessages"
            @click="deleteAllNotificationMessages()"
            class="tw:text-sm tw:text-indigo-400 tw:hover:text-indigo-300 tw:hover:underline tw:transition-colors tw:cursor-pointer tw:disabled:opacity-50 tw:font-bold"
          >
            Clear All
          </button>
        </div>

        <div v-if="userNotificationsEnabled" class="notification-message-list">
          <div v-if="notificationMessageItemsLoading" key="loading" class="tw:flex tw:justify-center tw:py-6">
            <spinner />
          </div>
          <div v-else-if="notificationMessageItemsLoadLastError" key="error" class="tw:text-rose-400 tw:text-sm tw:py-4 tw:flex tw:items-center tw:gap-2">
            <AlertTriangle class="tw:w-4 tw:h-4 tw:text-rose-400 tw:shrink-0" />
            <span>{{ notificationMessageItemsLoadLastError }}</span>
          </div>
          <div v-else key="items" class="tw:space-y-3 tw:max-h-[280px] tw:overflow-y-auto tw:pr-1">
            <div v-if="!notificationMessageItems || notificationMessageItems.length === 0" class="tw:text-slate-500 tw:text-sm tw:py-6 tw:text-center">
              No notifications.
            </div>
            <div
              v-for="item in notificationMessageItems"
              :key="item.id"
              class="tw:flex tw:gap-3 tw:p-2 tw:rounded-lg tw:hover:bg-slate-800/40 tw:transition-colors tw:relative tw:group"
              :class="[item.unread ? 'tw:bg-indigo-500/5' : '']"
            >
              <div class="tw:flex-1 tw:min-w-0">
                <div class="tw:flex tw:items-center tw:justify-between tw:gap-2">
                  <span class="tw:text-sm tw:font-bold tw:text-slate-200">{{ item.title }}</span>
                  <div class="tw:flex tw:items-center tw:gap-2 tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity tw:shrink-0">
                    <button
                      class="tw:text-slate-500 tw:hover:text-slate-300 tw:cursor-pointer tw:text-sm"
                      @click="toggleNotificationMessageUnread(item)"
                      :disabled="item._togglingUnread"
                    >
                      <component :is="item._togglingUnread ? 'Loader2' : (item.unread ? 'Mail' : 'MailOpen')" :class="{ 'animate-spin': item._togglingUnread }" class="tw:w-4 tw:h-4" />
                    </button>
                    <button
                      class="tw:text-slate-500 tw:hover:text-rose-400 tw:cursor-pointer tw:text-sm"
                      @click="deleteNotificationMessage(item)"
                      :disabled="item._deleting"
                    >
                      <component :is="item._deleting ? 'Loader2' : 'X'" :class="{ 'animate-spin': item._deleting }" class="tw:w-4 tw:h-4" />
                    </button>
                  </div>
                </div>
                <p class="tw:text-sm tw:text-slate-400 tw:mt-1 tw:leading-relaxed">{{ item.body }}</p>
                <div class="tw:flex tw:items-center tw:justify-between tw:mt-2 tw:text-2xs tw:text-slate-500 tw:font-mono">
                  <span v-if="item.orgId !== organizationId" class="tw:text-amber-500/80">{{ item.orgName }}</span>
                  <span>{{ formatDate(item.timestamp) }} {{ formatTime(item.timestamp) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="userNotificationsEnabled" class="tw:border-t tw:border-slate-800/60 tw:pt-3 tw:mt-3 tw:space-y-2.5">
          <span v-if="notificationMessageItemsHasMore" class="tw:block tw:text-2xs tw:text-slate-500">
            Showing most recent {{ notificationMessageItems.length }} messages.
          </span>
          <router-link
            :to="notificationMessagesLink"
            @click="closePopover"
            class="tw:block tw:text-sm tw:text-indigo-400 tw:hover:text-indigo-300 tw:font-semibold tw:transition-colors"
          >
            See all notification messages
          </router-link>
          <button
            v-if="hasNotificationFeature && orgUserId"
            @click="openNotificationPreferences(); closePopover()"
            class="tw:block tw:text-sm tw:text-slate-400 tw:hover:text-slate-200 tw:hover:underline tw:transition-colors tw:cursor-pointer"
          >
            Notification preferences
          </button>
          <label class="tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-slate-300">
            <input
              type="checkbox"
              :checked="showAllOrgs"
              @change="setShowAllOrgs($event.target.checked)"
              :disabled="notificationMessageItemsLoading"
              class="tw:h-3.5 tw:w-3.5 tw:rounded tw:border-slate-700 tw:bg-slate-800 tw:text-indigo-500 focus:tw:ring-indigo-500/40"
            >
            <span class="tw:select-none">Show all organizations</span>
          </label>
        </div>

        <div v-if="summaryCountDisplay" class="tw:border-t tw:border-slate-800/60 tw:pt-3 tw:mt-3 tw:space-y-3">
          <div v-if="notificationMessageCountDisplay" class="tw:rounded-lg tw:border tw:border-slate-800/70 tw:bg-slate-950/40 tw:p-2.5 tw:text-sm tw:leading-relaxed">
            <div class="tw:flex tw:items-start tw:gap-2">
              <CircleAlert class="tw:w-3.5 tw:h-3.5 tw:text-amber-400 tw:mt-0.5 tw:shrink-0" />
              <div>
                There {{ notificationMessageCount > 1 ? 'are' : 'is' }} {{ notificationMessageCountDisplay }} unread Notification {{ inflect('message', notificationMessageCount) }}.
                <div>
                  <router-link :to="notificationMessagesLink" @click="closePopover" class="tw:text-indigo-400 tw:hover:text-indigo-300">View notifications</router-link>
                </div>
              </div>
            </div>
          </div>

          <div v-if="disciplineInfractionCountDisplay" class="tw:rounded-lg tw:border tw:border-slate-800/70 tw:bg-slate-950/40 tw:p-2.5 tw:text-sm tw:leading-relaxed">
            <div class="tw:flex tw:items-start tw:gap-2">
              <CircleAlert class="tw:w-3.5 tw:h-3.5 tw:text-amber-400 tw:mt-0.5 tw:shrink-0" />
              <div>
                There {{ disciplineInfractionCount > 1 ? 'are' : 'is' }} {{ disciplineInfractionCountDisplay }} open Attendance {{ inflect('Infraction', disciplineInfractionCount) }}.
                <div>
                  <router-link :to="{ name: 'discipline-infractions' }" @click="closePopover" class="tw:text-indigo-400 tw:hover:text-indigo-300">View infractions</router-link>
                </div>
              </div>
            </div>
          </div>

          <div v-if="disciplineEventCountDisplay" class="tw:rounded-lg tw:border tw:border-slate-800/70 tw:bg-slate-950/40 tw:p-2.5 tw:text-sm tw:leading-relaxed">
            <div class="tw:flex tw:items-start tw:gap-2">
              <CircleAlert class="tw:w-3.5 tw:h-3.5 tw:text-amber-400 tw:mt-0.5 tw:shrink-0" />
              <div>
                There {{ disciplineEventCount > 1 ? 'are' : 'is' }} {{ disciplineEventCountDisplay }} open Attendance {{ inflect('Event', disciplineEventCount) }}.
                <div>
                  <router-link :to="{ name: 'discipline-events' }" @click="closePopover" class="tw:text-indigo-400 tw:hover:text-indigo-300">View events</router-link>
                </div>
              </div>
            </div>
          </div>

          <div v-if="missingPunchRequestCountDisplay" class="tw:rounded-lg tw:border tw:border-slate-800/70 tw:bg-slate-950/40 tw:p-2.5 tw:text-sm tw:leading-relaxed">
            <div class="tw:flex tw:items-start tw:gap-2">
              <CircleAlert class="tw:w-3.5 tw:h-3.5 tw:text-amber-400 tw:mt-0.5 tw:shrink-0" />
              <div>
                There {{ missingPunchRequestCount > 1 ? 'are' : 'is' }} {{ missingPunchRequestCountDisplay }} pending Missed Punch {{ inflect('request', missingPunchRequestCount) }}.
                <div>
                  <router-link :to="{ name: 'missed-punch-list' }" @click="closePopover" class="tw:text-indigo-400 tw:hover:text-indigo-300">View requests</router-link>
                </div>
              </div>
            </div>
          </div>

          <div v-if="shiftExtendRequestCountDisplay" class="tw:rounded-lg tw:border tw:border-slate-800/70 tw:bg-slate-950/40 tw:p-2.5 tw:text-sm tw:leading-relaxed">
            <div class="tw:flex tw:items-start tw:gap-2">
              <CircleAlert class="tw:w-3.5 tw:h-3.5 tw:text-amber-400 tw:mt-0.5 tw:shrink-0" />
              <div>
                There {{ shiftExtendRequestCount > 1 ? 'are' : 'is' }} {{ shiftExtendRequestCountDisplay }} pending Shift Extend {{ inflect('request', shiftExtendRequestCount) }}.
                <div>
                  <router-link :to="{ name: 'shift-extend-requests' }" @click="closePopover" class="tw:text-indigo-400 tw:hover:text-indigo-300">View requests</router-link>
                </div>
              </div>
            </div>
          </div>

          <div v-if="timeOffRequestCountDisplay" class="tw:rounded-lg tw:border tw:border-slate-800/70 tw:bg-slate-950/40 tw:p-2.5 tw:text-sm tw:leading-relaxed">
            <div class="tw:flex tw:items-start tw:gap-2">
              <CircleAlert class="tw:w-3.5 tw:h-3.5 tw:text-amber-400 tw:mt-0.5 tw:shrink-0" />
              <div>
                There {{ timeOffRequestCount > 1 ? 'are' : 'is' }} {{ timeOffRequestCountDisplay }} pending Time Off {{ inflect('request', timeOffRequestCount) }}.
                <div>
                  <router-link :to="{ name: 'timeoff-requests' }" @click="closePopover" class="tw:text-indigo-400 tw:hover:text-indigo-300">View requests</router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="tw:border-t tw:border-slate-800/60 tw:pt-3 tw:mt-3 tw:text-sm tw:text-slate-400">
          You do not currently have any notifications.
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { useDialog } from 'primevue/usedialog'
import { inflect } from 'inflection'
import moment from 'moment-timezone'
import Spinner from '@/components/Spinner.vue'
import { extractErrorMessage } from '@/utils/misc'
import { openNotificationPreferences } from '@/components/notification-preferences-dialog'
import {
  Bell,
  AlertTriangle,
  CircleAlert,
  Mail,
  MailOpen,
  Loader2,
  X
} from '@lucide/vue'

export default {
  name: 'NotificationBell',
  components: {
    Spinner,
    Bell,
    AlertTriangle,
    CircleAlert,
    Mail,
    MailOpen,
    Loader2,
    X
  },
  setup () {
    const dialog = useDialog()
    return { dialog }
  },
  data () {
    return {
      inflect,
      popoverOpen: false
    }
  },
  computed: {
    ...mapState(['organizationId', 'orgUserId']),
    ...mapGetters(['hasNotificationFeature', 'userNotificationsEnabled']),
    ...mapGetters('dashboardNotifications', [
      'summaryCountDisplay',
      'timeOffRequestCount',
      'timeOffRequestCountDisplay',
      'missingPunchRequestCount',
      'missingPunchRequestCountDisplay',
      'shiftExtendRequestCount',
      'shiftExtendRequestCountDisplay',
      'disciplineInfractionCountDisplay',
      'disciplineEventCountDisplay',
      'disciplineInfractionCount',
      'disciplineEventCount',
      'notificationMessageCount',
      'notificationMessageCountDisplay',
      'notificationMessageItemsLoading',
      'notificationMessageItemsHasMore'
    ]),
    ...mapState('dashboardNotifications', [
      'notificationMessageItems',
      'notificationMessageItemsLoadLastError',
      'showAllOrgs'
    ]),
    ...mapState('dashboardNotifications', {
      deletingAllNotificationMessages: state => state.deletingAll
    }),
    ...mapGetters('formatPreferences', ['formatShortDate', 'formatTime']),
    notificationMessagesLink () {
      return this.$store.getters.insideOrgNamespace
        ? { name: 'org-notification-messages', params: { orgId: this.$store.state.organizationId } }
        : '/notifications'
    }
  },
  watch: {
    popoverOpen (popoverOpen) {
      if (popoverOpen && this.userNotificationsEnabled) {
        this.$store.dispatch('dashboardNotifications/loadNotificationMessageItems')
      }
    }
  },
  methods: {
    togglePopover () {
      this.popoverOpen = !this.popoverOpen
    },
    closePopover () {
      this.popoverOpen = false
    },
    formatDate (timestamp) {
      const m = moment(timestamp)
      if (m.isAfter(moment().startOf('day'))) {
        return 'Today'
      } else if (m.isAfter(moment().startOf('year'))) {
        return m.format('MMM D')
      } else {
        return this.formatShortDate(m)
      }
    },
    async deleteNotificationMessage (item) {
      if (item._deleting || item._togglingUnread) return
      item._deleting = true
      try {
        await this.$store.dispatch('dashboardNotifications/deleteNotificationMessage', item.id)
        const masterItems = this.$store.state.notificationMessageMasterDetail.masterItems.filter(i => i.id !== item.id)
        this.$store.commit('notificationMessageMasterDetail/masterItemsChanged', {
          masterItems: [...masterItems],
          cursor: this.$store.state.notificationMessageMasterDetail.cursor
        })
      } catch (error) {
        this.$toast.error(extractErrorMessage(error))
      } finally {
        item._deleting = false
      }
    },
    async toggleNotificationMessageUnread (item) {
      if (item._togglingUnread || item._deleting) return
      item._togglingUnread = true
      try {
        const unread = !item.unread
        await this.$store.dispatch('dashboardNotifications/setNotificationMessageUnread', { itemId: item.id, unread })
        const { masterItems, cursor } = this.$store.state.notificationMessageMasterDetail
        const masterItem = masterItems.find(i => i.id === item.id)
        if (masterItem) {
          masterItem.unread = unread
          this.$store.commit('notificationMessageMasterDetail/masterItemsChanged', {
            masterItems: [...masterItems],
            cursor
          })
        }
      } catch (error) {
        this.$toast.error(extractErrorMessage(error))
      } finally {
        item._togglingUnread = false
      }
    },
    setShowAllOrgs (showAllOrgs) {
      this.$store.dispatch('dashboardNotifications/setShowAllOrgs', showAllOrgs)
    },
    async deleteAllNotificationMessages () {
      if (this.deletingAllNotificationMessages) return
      try {
        await this.$store.dispatch('dashboardNotifications/deleteAllNotificationMessages')
        this.$store.commit('notificationMessageMasterDetail/masterItemsChanged', {
          masterItems: [],
          cursor: null
        })
      } catch (error) {
        this.$toast.error(extractErrorMessage(error))
      }
    },
    openNotificationPreferences () {
      openNotificationPreferences(this.dialog)
    }
  }
}
</script>

<style scoped>
/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s cubic-bezier(0, 0, 0.2, 1);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

/* Reset button focus outlines/shadows from Bootstrap clashing */
button {
  border: 0 solid transparent;
}
button:focus,
button:active {
  outline: none !important;
  box-shadow: none !important;
}
</style>
